// frontend/src/pages/InventarioPage.js
/**
 * Pagina per la gestione dell'inventario fisico.
 * Funziona offline-first con IndexedDB per supportare connessioni 4G instabili.
 * 
 * Caratteristiche:
 * - Scansione a raffica di barcode
 * - Suoni di conferma/errore
 * - Salvataggio locale con IndexedDB
 * - Preview e conferma prima di aggiornare il database
 * - Report discrepanze dettagliato
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Chip,
    Alert,
    AlertTitle,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Grid,
    Tabs,
    Tab,
    Badge,
    Switch,
    FormControlLabel,
    Snackbar,
    Table as MuiTable,
    TableBody as MuiTableBody,
    TableCell as MuiTableCell,
    TableContainer as MuiTableContainer,
    TableHead as MuiTableHead,
    TableRow as MuiTableRow,
} from '@mui/material';
import {
    QrCodeScanner as ScannerIcon,
    Delete as DeleteIcon,
    Sync as SyncIcon,
    Check as CheckIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
    VolumeUp as VolumeUpIcon,
    VolumeOff as VolumeOffIcon,
    Save as SaveIcon,
    PlayArrow as StartIcon,
    Stop as StopIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
} from '@mui/icons-material';
import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material'; // Aggiungo per il report
import api from '../api';
import InventarioMultiSKUDialog from '../components/InventarioMultiSKUDialog';
import { Table, Column, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css'; // Stile base richiesto

// ==================== COSTANTI ====================

const BEEP_SUCCESS_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleYpJT5/NuHg5OFVuhJV3TjJFaIuPaUM6UG2AjHJCN0tjfoaAbEk+WnV8fHVfTExlfHx5cV5OT2N3dn51ZVZRXnFze3VrX1Rbb3J0dXNpXFdib3FzdXFoXllhbW9xcW9oXltkbGxubmpkXl5mamtram5oZWFla2pra2toZ2RnaWpqa2lpZ2VoaWpqamlpZ2doaWlpaWloZ2hpaWlpaWhpaGhpaWlpaWhoaGhoaGhoaGhoaGhoaGhoaGho';
const BEEP_ERROR_URL = 'data:audio/wav;base64,UklGRl9vT19teleYpGVFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YTQOAACAf4B/f39/gIB/f4CAf4CAgH+AgIB/gICAf4CAgH+AgICAf4CAgICAgICAf4CAgIB/gIB/f4CAf39/f4B/f39/f39/f4B/f39/f39/f39/f39/f39/f39/f39/f39/f39/';
const BEEP_NOT_FOUND_URL = 'data:audio/wav;base64,UklGRjIGAABXQVZFZm10IBAAAAABAAEAIlYAAFkuAAABAAgAZGF0YQ4GAACBAS8BCVQBe4UBf4OBgYB/f4CAgH9/gH+Af4B/gH+Af4B/gH+Af4B/gH+Af4B/gH+Af4B/gH+AgH+Af4B/gH+Af4B/gH+Af4B/gH+Af4B/f4B/f39/f39/f39/f39/f39/f39/f4B/f4B/f4B/gH+Af4B/gH+AgH+AgH+AgICAgICAgICAgICAg4ODg4ODg4ODg4ODg4ODg4ODg4ODg4KCgoKCgoKCgoKCgoKCgoGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYCAgICAgICAgIB/f39/f39/';

const DB_NAME = 'InventarioDB';
const DB_VERSION = 1;
const STORE_NAME = 'scansioni';

// ==================== INDEXEDDB HELPERS ====================

const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
            }
        };
    });
};

const saveSessionToIndexedDB = async (sessionData) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.put(sessionData);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const loadSessionFromIndexedDB = async (id = 'current') => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const clearSessionFromIndexedDB = async (id = 'current') => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

// ==================== AUDIO HELPERS ====================

const playBeep = (type = 'success') => {
    try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        if (type === 'success') {
            oscillator.frequency.value = 1000; // Beep acuto per successo
            oscillator.type = 'sine';
            gainNode.gain.value = 0.3;
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.1);
        } else if (type === 'duplicate') {
            oscillator.frequency.value = 800; // Beep medio per duplicato (incrementa conteggio)
            oscillator.type = 'sine';
            gainNode.gain.value = 0.2;
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.05);
        } else if (type === 'not_found') {
            // Due beep bassi per non trovato
            oscillator.frequency.value = 300;
            oscillator.type = 'square';
            gainNode.gain.value = 0.4;
            oscillator.start();
            setTimeout(() => {
                oscillator.stop();
            }, 200);
        } else if (type === 'error') {
            // Beep lungo e basso per errore
            oscillator.frequency.value = 200;
            oscillator.type = 'sawtooth';
            gainNode.gain.value = 0.5;
            oscillator.start();
            oscillator.stop(audioContext.currentTime + 0.5);
        }
    } catch (e) {
        console.warn('Audio non supportato:', e);
    }
};

// ==================== COMPONENTE PRINCIPALE ====================

const InventarioPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Stati principali
    const [scanning, setScanning] = useState(false);
    const [conteggi, setConteggi] = useState({}); // { barcode: { quantita, titolo?, tipo?, found? } }
    const [conteggiDettagliati, setConteggiDettagliati] = useState([]); // Array di ConteggioSKUItem
    const [categoria, setCategoria] = useState('');
    const [note, setNote] = useState('');
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [lastScanned, setLastScanned] = useState(null);
    
    // Stati per selezione zona
    const [zonaSelezionata, setZonaSelezionata] = useState('');
    const [zoneDisponibili, setZoneDisponibili] = useState([]);
    const [loadingZone, setLoadingZone] = useState(false);
    
    // Stati per multi-SKU dialog
    const [showMultiSKUDialog, setShowMultiSKUDialog] = useState(false);
    const [currentBarcodeData, setCurrentBarcodeData] = useState(null);

    // Stati per creazione nuovo prodotto
    const [showCreateProductDialog, setShowCreateProductDialog] = useState(false);
    const [barcodeToCreate, setBarcodeToCreate] = useState(null);
    
    // Stati per il report
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [reportTab, setReportTab] = useState(0);
    const [azzeraNonScansionati, setAzzeraNonScansionati] = useState(false);
    
    // Stati per conferma e messaggi
    const [confirmCommit, setConfirmCommit] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [commitResult, setCommitResult] = useState(null);
    
    // Stati per storico
    const [showHistory, setShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [miraklReport, setMiraklReport] = useState(null); // NUOVO: Stato per il report Mirakl
    
    // Ref per il campo input
    const inputRef = useRef(null);
    
    // Variabile sicura per evitare crash se conteggi è null
    const { newProduct, wasScanning } = location.state || {};
    
    // ==================== EFFECTS ====================
    
    // Carica zone disponibili all'avvio
    useEffect(() => {
        const loadZone = async () => {
            setLoadingZone(true);
            try {
                const response = await api.get('/inventario/zone');
                setZoneDisponibili(response.data || []);
            } catch (error) {
                console.error('Errore caricamento zone:', error);
                setSnackbar({
                    open: true,
                    message: 'Errore caricamento zone inventario',
                    severity: 'error'
                });
            } finally {
                setLoadingZone(false);
            }
        };
        loadZone();
    }, []);
    
    // Carica sessione salvata da IndexedDB all'avvio
    useEffect(() => {
        const loadSavedSession = async () => {
            try {
                const saved = await loadSessionFromIndexedDB('current');
                if (saved && saved.conteggiDettagliati && saved.conteggiDettagliati.length > 0) {
                    setConteggiDettagliati(saved.conteggiDettagliati);
                    setCategoria(saved.categoria || '');
                    setNote(saved.note || '');
                    setZonaSelezionata(saved.zonaSelezionata || '');
                    setSnackbar({
                        open: true,
                        message: `Sessione precedente ripristinata: ${saved.conteggiDettagliati.length} prodotti (SKU)`,
                        severity: 'info'
                    });
                }
            } catch (e) {
                console.error('Errore caricamento sessione:', e);
            }
        };
        loadSavedSession();
    }, []);
    
    // Salva sessione in IndexedDB quando cambiano i conteggi
    useEffect(() => {
        if (conteggiDettagliati.length > 0) {
            saveSessionToIndexedDB({
                id: 'current',
                conteggiDettagliati,
                categoria,
                zonaSelezionata,
                note,
                lastUpdate: new Date().toISOString()
            }).catch(console.error);
        }
    }, [conteggiDettagliati, categoria, note, zonaSelezionata]);
    
    // Focus sull'input quando in modalità scansione
    useEffect(() => {
        if (scanning && inputRef.current) {
            inputRef.current.focus();
        }
    }, [scanning]);

    // Gestisce il ritorno dalla pagina di creazione nuovo prodotto
    useEffect(() => {
        if (newProduct) {
            // La risposta API potrebbe avere una struttura tipo { film: {...} } o { musica: {...} }
            const productData = newProduct.film || newProduct.musica || newProduct;
            
            if (productData && productData.sku) {
                setConteggiDettagliati(prev => {
                    const existing = prev.find(c => c.sku === productData.sku);
                    if (existing) {
                        return prev.map(c => 
                            c.sku === productData.sku 
                                ? { ...c, quantita: c.quantita + 1 } 
                                : c
                        );
                    } else {
                        return [...prev, {
                            sku: productData.sku,
                            barcode: productData.barcode,
                            quantita: 1,
                            ubicazione: productData.ubicazione,
                            titolo: productData.titolo,
                        }];
                    }
                });

                if (soundEnabled) playBeep('success');
                setSnackbar({
                    open: true,
                    message: `"${productData.titolo}" aggiunto all'inventario con quantità 1.`,
                    severity: 'success'
                });

                // Pulisci solo il newProduct, mantenendo wasScanning se presente
                const { newProduct: _, ...restState } = location.state;
                navigate(location.pathname, { replace: true, state: restState });
            }
        }
    }, [newProduct, navigate, soundEnabled]);

    // Riattiva la modalità scansione al ritorno
    useEffect(() => {
        if (wasScanning) {
            setScanning(true);
        }
    }, [wasScanning]);
    
    // ==================== HANDLERS ====================
    
    const handleBarcodeSubmit = useCallback(async (e) => {
        e.preventDefault();
        const barcode = barcodeInput.trim();
        
        if (!barcode) return;
        
        setBarcodeInput('');
        
        // Lookup barcode per verificare se ci sono SKU multipli
        try {
            // Passa zona_selezionata come query parameter
            const params = zonaSelezionata ? { zona_selezionata: zonaSelezionata } : {};
            const response = await api.get(`/inventario/lookup/${barcode}`, { params });
            
            if (!response.data.found) {
                // Barcode non trovato -> Chiedi se si vuole creare un nuovo prodotto
                if (soundEnabled) playBeep('not_found');
                setBarcodeToCreate(barcode);
                setShowCreateProductDialog(true);
            } else if (response.data.ubicazione_errata) {
                // Ubicazione errata - mostra alert
                if (soundEnabled) playBeep('error');
                
                const sku = response.data.sku_multipli[0];
                setSnackbar({
                    open: true,
                    message: `⚠️ UBICAZIONE ERRATA! ${sku.titolo} si trova in "${sku.ubicazione}", non in "${zonaSelezionata}"`,
                    severity: 'warning'
                });
                
                // Mostra dialog per correggere o ignorare
                setCurrentBarcodeData(response.data);
                setShowMultiSKUDialog(true);
            } else if (response.data.totale_sku > 1) {
                // SKU multipli - apri dialog per selezione
                if (soundEnabled) playBeep('success');
                setCurrentBarcodeData(response.data);
                setShowMultiSKUDialog(true);
            } else {
                // SKU singolo - conteggio diretto
                if (soundEnabled) {
                    playBeep('success');
                }
                
                const sku = response.data.sku_multipli[0];
                
                // Aggiungi ai conteggi dettagliati
                setConteggiDettagliati(prev => {
                    const existing = prev.find(c => c.sku === sku.sku);
                    if (existing) {
                        playBeep('duplicate');
                        return prev.map(c => 
                            c.sku === sku.sku 
                                ? { ...c, quantita: c.quantita + 1 }
                                : c
                        );
                    } else {
                        return [...prev, {
                            sku: sku.sku,
                            barcode: barcode,
                            quantita: 1,
                            ubicazione: sku.ubicazione,
                            titolo: sku.titolo
                        }];
                    }
                });
            }
            
            setLastScanned({ barcode, timestamp: new Date() });
        } catch (error) {
            console.error('Errore lookup barcode:', error);
            if (soundEnabled) playBeep('error');
            setSnackbar({
                open: true,
                message: `Errore di rete durante la ricerca: ${error.message}`,
                severity: 'error'
            });
        }
        
        // Focus immediato per la prossima scansione
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [barcodeInput, soundEnabled, zonaSelezionata]);
    
    const handleRemoveBarcode = (skuToRemove) => {
        setConteggiDettagliati(prev => prev.filter(item => item.sku !== skuToRemove));
    };
    
    const handleMultiSKUConfirm = (data) => {
        const { sku, barcode, quantita, ubicazione, titolo } = data;
        
        // Non usiamo più lo stato 'conteggi', ma potremmo tenerlo per retrocompatibilità visiva se necessario.
        // Per ora, ci concentriamo solo su conteggiDettagliati
        
        // Aggiungi/aggiorna conteggi dettagliati per SKU
        setConteggiDettagliati(prev => {
            const existing = prev.find(c => c.sku === sku);
            if (existing) {
                return prev.map(c => 
                    c.sku === sku 
                        ? { ...c, quantita: c.quantita + quantita, ubicazione: ubicazione } // Titolo e supporto non cambiano se SKU esiste
                        : c
                );
            } else {
                return [...prev, {
                    sku: sku,
                    barcode: barcode,
                    quantita: quantita,
                    ubicazione: ubicazione,
                    titolo: titolo, // Aggiungi titolo
                }];
            }
        });
        
        setShowMultiSKUDialog(false);
        setCurrentBarcodeData(null);
        
        // Focus sull'input per prossima scansione
        if (inputRef.current && scanning) {
            setTimeout(() => inputRef.current.focus(), 100);
        }
    };
    
    const handleDecrementQuantita = (skuToDecrement) => {
        setConteggiDettagliati(prev => 
            prev.map(item =>
                item.sku === skuToDecrement
                    ? { ...item, quantita: item.quantita - 1 }
                    : item
            ).filter(item => item.quantita > 0) // Rimuove l'elemento se la quantità va a 0
        );
    };
    
    const handleClearAll = () => {
        if (window.confirm('Sei sicuro di voler cancellare tutte le scansioni?')) {
            setConteggi({}); // Pulisce anche il vecchio stato per sicurezza
            setConteggiDettagliati([]);
            clearSessionFromIndexedDB('current').catch(console.error);
            setSnackbar({ open: true, message: 'Scansioni cancellate', severity: 'warning' });
        }
    };
    
    const handlePreviewReport = async () => {
        if (conteggiDettagliati.length === 0) {
            setSnackbar({ open: true, message: 'Nessun prodotto scansionato', severity: 'warning' });
            return;
        }
        
        setLoading(true);
        try {
            const response = await api.post('/inventario/preview', {
                conteggi_dettagliati: conteggiDettagliati,
                categoria: categoria || null,
                zona: zonaSelezionata || null,
                note: note || null
            });
            
            setReportData(response.data);
            setShowReportDialog(true);
        } catch (error) {
            console.error('Errore preview:', error);
            if (soundEnabled) playBeep('error');
            setSnackbar({
                open: true,
                message: `Errore: ${error.response?.data?.detail || error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };
    
    const handleCommitInventario = async () => {
        setLoading(true);
        try {
            const response = await api.post('/inventario/commit', {
                conteggi_dettagliati: conteggiDettagliati,
                categoria: categoria || null,
                zona: zonaSelezionata || null,
                note: note || null,
                aggiorna_non_scansionati_a_zero: azzeraNonScansionati
            });
            
            setCommitResult(response.data);
            setConfirmCommit(false);
            setShowReportDialog(false);
            
            // Pulisci sessione e mostra report Mirakl se presente
            setConteggi({});
            setConteggiDettagliati([]); // Pulisci anche il nuovo stato
            await clearSessionFromIndexedDB('current');
            
            if (response.data.mirakl_sync_report) {
                setMiraklReport(response.data.mirakl_sync_report);
            }

            if (soundEnabled) playBeep('success');
            setSnackbar({
                open: true,
                message: `Inventario completato! ${response.data.prodotti_aggiornati} prodotti aggiornati.`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Errore commit:', error);
            if (soundEnabled) playBeep('error');
            setSnackbar({
                open: true,
                message: `Errore: ${error.response?.data?.detail || error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };
    
    const handleLoadHistory = async () => {
        setLoadingHistory(true);
        try {
            const response = await api.get('/inventario/history');
            setHistoryData(response.data.items || []);
            setShowHistory(true);
        } catch (error) {
            console.error('Errore caricamento storico:', error);
            setSnackbar({
                open: true,
                message: `Errore: ${error.response?.data?.detail || error.message}`,
                severity: 'error'
            });
        } finally {
            setLoadingHistory(false);
        }
    };
    
    const handleExportCSV = () => {
        const rows = conteggiDettagliati.map(item => ({
            sku: item.sku,
            barcode: item.barcode,
            quantita: item.quantita,
            ubicazione: item.ubicazione || ''
        }));
        
        const csv = 'sku,barcode,quantita,ubicazione\n' + rows.map(r => `${r.sku},${r.barcode},${r.quantita},"${r.ubicazione.replace(/"/g, '""')}"`).join('\n');
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }); // Add BOM for Excel
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventario_dettagliato_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };
    
    // ==================== CALCOLI ====================
    
    const totaleScansioni = conteggiDettagliati.reduce((sum, d) => sum + d.quantita, 0);
    const totaleProdotti = conteggiDettagliati.length;
    
    // Prepara i dati per la tabella virtualizzata (basata su SKU)
    const scanList = [...conteggiDettagliati].sort((a, b) => b.quantita - a.quantita);

    // Renderer per la colonna azioni
    const actionsCellRenderer = ({ rowData }) => {
        return (
            <Box>
                <IconButton 
                    size="small" 
                    onClick={() => handleDecrementQuantita(rowData.sku)}
                    title="Rimuovi 1"
                >
                    -1
                </IconButton>
                <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => handleRemoveBarcode(rowData.sku)}
                    title="Rimuovi tutto"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    // ==================== RENDER ====================
    
    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    <ScannerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Inventario Fisico
                </Typography>
                <Box>
                    <Tooltip title={soundEnabled ? 'Disattiva suoni' : 'Attiva suoni'}>
                        <IconButton onClick={() => setSoundEnabled(!soundEnabled)} color={soundEnabled ? 'primary' : 'default'}>
                            {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Storico inventari">
                        <IconButton onClick={handleLoadHistory} disabled={loadingHistory}>
                            {loadingHistory ? <CircularProgress size={24} /> : <HistoryIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            
            {/* Configurazione */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container columns={12} spacing={2} alignItems="center">
                    <Grid span={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Zona Inventario</InputLabel>
                            <Select
                                value={zonaSelezionata}
                                onChange={(e) => setZonaSelezionata(e.target.value)}
                                label="Zona Inventario"
                                disabled={scanning || loadingZone}
                            >
                                <MenuItem value="">Tutte le zone</MenuItem>
                                {zoneDisponibili.map((zona) => (
                                    <MenuItem key={zona.nome_zona} value={zona.nome_zona}>
                                        {zona.nome_zona} {zona.conteggio_ubicazioni > 1 && `(${zona.conteggio_ubicazioni} ubicazioni)`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid span={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Categoria (opzionale)</InputLabel>
                            <Select
                                value={categoria}
                                onChange={(e) => setCategoria(e.target.value)}
                                label="Categoria (opzionale)"
                                disabled={scanning}
                            >
                                <MenuItem value="">Tutte le categorie</MenuItem>
                                <MenuItem value="film">Solo Film</MenuItem>
                                <MenuItem value="musica">Solo Musica</MenuItem>
                                <MenuItem value="collectible">Solo Collectible</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid span={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Note (opzionale)"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={scanning}
                        />
                    </Grid>
                    <Grid xs={12} md={3}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant={scanning ? 'outlined' : 'contained'}
                                color={scanning ? 'error' : 'primary'}
                                startIcon={scanning ? <StopIcon /> : <StartIcon />}
                                onClick={() => setScanning(!scanning)}
                                fullWidth
                                disabled={!zonaSelezionata && scanning === false}
                            >
                                {scanning ? 'Ferma Scansione' : 'Avvia Scansione'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
            
            {/* Area Scansione */}
            {scanning && (
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h6" gutterBottom>
                        📷 Modalità Scansione Attiva
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Scansiona i barcode con il lettore. Il focus è automatico sul campo di input.
                    </Typography>
                    <form onSubmit={handleBarcodeSubmit}>
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            variant="outlined"
                            placeholder="Scansiona o digita barcode..."
                            value={barcodeInput}
                            onChange={(e) => setBarcodeInput(e.target.value)}
                            autoFocus
                            sx={{
                                bgcolor: 'white',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '1.5rem'
                                }
                            }}
                        />
                    </form>
                    {lastScanned && (
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                            Ultimo: {lastScanned.barcode} ({new Date(lastScanned.timestamp).toLocaleTimeString()})
                        </Typography>
                    )}
                </Paper>
            )}
            
            {/* Statistiche */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid xs={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="primary">{totaleScansioni}</Typography>
                            <Typography variant="body2" color="text.secondary">Scansioni totali</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid xs={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="secondary">{totaleProdotti}</Typography>
                            <Typography variant="body2" color="text.secondary">Prodotti unici (SKU)</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="h6">Azioni</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {totaleProdotti > 0 ? 'Sessione in corso - dati salvati localmente' : 'Nessun prodotto scansionato'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleExportCSV}
                                        disabled={totaleProdotti === 0}
                                    >
                                        Esporta CSV
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleClearAll}
                                        disabled={totaleProdotti === 0}
                                    >
                                        Cancella
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                                        onClick={handlePreviewReport}
                                        disabled={totaleProdotti === 0 || loading}
                                    >
                                        Crea Report
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            
            {/* Lista Scansioni */}
            {totaleProdotti > 0 && (
                <Paper sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom>
                        Prodotti Scansionati ({totaleProdotti})
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                        <AutoSizer>
                            {({ height, width }) => (
                                <Table
                                    width={width}
                                    height={height}
                                    headerHeight={30}
                                    rowHeight={40}
                                    rowCount={scanList.length}
                                    rowGetter={({ index }) => scanList[index]}
                                >
                                    <Column
                                        label="SKU"
                                        dataKey="sku"
                                        width={width * 0.3}
                                        headerRenderer={({ label }) => <div>{label}</div>}
                                        cellDataGetter={({ dataKey, rowData }) => rowData[dataKey]}
                                        cellRenderer={({ cellData }) => <div>{cellData}</div>}
                                    />
                                    <Column
                                        label="Barcode"
                                        dataKey="barcode"
                                        width={width * 0.2}
                                        headerRenderer={({ label }) => <div>{label}</div>}
                                        cellDataGetter={({ dataKey, rowData }) => rowData[dataKey]}
                                        cellRenderer={({ cellData }) => <div>{cellData}</div>}
                                    />
                                    <Column
                                        label="Titolo"
                                        dataKey="titolo"
                                        width={width * 0.4}
                                        headerRenderer={({ label }) => <div>{label}</div>}
                                        cellDataGetter={({ dataKey, rowData }) => rowData[dataKey]}
                                        cellRenderer={({ cellData }) => <div title={cellData}>{cellData}</div>}
                                    />
                                    <Column
                                        label="Quantità"
                                        dataKey="quantita"
                                        width={width * 0.1}
                                        headerRenderer={({ label }) => <div>{label}</div>}
                                        cellDataGetter={({ dataKey, rowData }) => rowData[dataKey]}
                                        cellRenderer={({ cellData }) => (
                                            <Box textAlign="center">
                                                <Chip label={cellData} color="primary" size="small" />
                                            </Box>
                                        )}
                                    />
                                    <Column
                                        label="Azioni"
                                        dataKey="sku"
                                        width={width * 0.1}
                                        headerRenderer={({ label }) => <div>{label}</div>}
                                        cellDataGetter={({ dataKey, rowData }) => rowData[dataKey]}
                                        cellRenderer={actionsCellRenderer}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Box>
                </Paper>
            )}
            
            {/* Dialog Report */}
            <Dialog 
                open={showReportDialog} 
                onClose={() => setShowReportDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Report Inventario
                    {reportData && (
                        <Typography variant="body2" color="text.secondary">
                            {reportData.totale_scansioni} scansioni su {reportData.totale_prodotti_scansionati} prodotti
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {reportData && (
                        <>
                            {/* Riepilogo */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid xs={6} md={2}>
                                    <Card sx={{ bgcolor: 'success.light' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                            <Typography variant="h5">{reportData.riepilogo.prodotti_trovati}</Typography>
                                            <Typography variant="caption">Trovati</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid xs={6} md={2}>
                                    <Card sx={{ bgcolor: 'info.light' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                            <Typography variant="h5">{reportData.riepilogo.discrepanze_positive}</Typography>
                                            <Typography variant="caption">Eccedenze</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid xs={6} md={2}>
                                    <Card sx={{ bgcolor: 'warning.light' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                            <Typography variant="h5">{reportData.riepilogo.discrepanze_negative}</Typography>
                                            <Typography variant="caption">Mancanze</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid xs={6} md={2}>
                                    <Card sx={{ bgcolor: 'error.light' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                            <Typography variant="h5">{reportData.riepilogo.barcode_non_trovati}</Typography>
                                            <Typography variant="caption">Non in DB</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid xs={6} md={2}>
                                    <Card sx={{ bgcolor: 'grey.300' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                            <Typography variant="h5">{reportData.riepilogo.prodotti_non_scansionati}</Typography>
                                            <Typography variant="caption">Non scansionati</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                            
                            {/* Tabs per le diverse sezioni */}
                            <Tabs value={reportTab} onChange={(e, v) => setReportTab(v)} sx={{ mb: 2 }}>
                                <Tab 
                                    label={
                                        <Badge badgeContent={reportData?.discrepanze?.length || 0} color="warning">
                                            Discrepanze
                                        </Badge>
                                    } 
                                />
                                <Tab 
                                    label={
                                        <Badge badgeContent={reportData?.barcode_non_trovati?.length || 0} color="error">
                                            Non trovati
                                        </Badge>
                                    } 
                                />
                                <Tab 
                                    label={
                                        <Badge badgeContent={reportData?.prodotti_non_scansionati?.length || 0} color="default">
                                            Non scansionati
                                        </Badge>
                                    } 
                                />
                            </Tabs>
                            
                            {/* Tab Discrepanze */}
                            {reportTab === 0 && (
                                <MuiTableContainer sx={{ maxHeight: 300 }}>
                                    <MuiTable stickyHeader>
                                        <MuiTableHead>
                                            <MuiTableRow>
                                                <MuiTableCell>Barcode</MuiTableCell>
                                                <MuiTableCell>Titolo</MuiTableCell>
                                                <MuiTableCell>Tipo</MuiTableCell>
                                                <MuiTableCell align="center">A Sistema</MuiTableCell>
                                                <MuiTableCell align="center">Contato</MuiTableCell>
                                                <MuiTableCell align="center">Differenza</MuiTableCell>
                                            </MuiTableRow>
                                        </MuiTableHead>
                                        <MuiTableBody>
                                            {(reportData?.discrepanze?.length || 0) === 0 ? (
                                                <MuiTableRow>
                                                    <MuiTableCell colSpan={6} align="center">
                                                        <CheckIcon color="success" /> Nessuna discrepanza
                                                    </MuiTableCell>
                                                </MuiTableRow>
                                            ) : (
                                                (reportData?.discrepanze || []).map((d, i) => (
                                                    <MuiTableRow key={i}>
                                                        <MuiTableCell>
                                                            <Typography variant="body2" fontFamily="monospace">{d.barcode}</Typography>
                                                        </MuiTableCell>
                                                        <MuiTableCell>{d.titolo}</MuiTableCell>
                                                        <MuiTableCell>
                                                            <Chip label={d.tipo_prodotto} size="small" />
                                                        </MuiTableCell>
                                                        <MuiTableCell align="center">{d.quantita_sistema}</MuiTableCell>
                                                        <MuiTableCell align="center">{d.quantita_contata}</MuiTableCell>
                                                        <MuiTableCell align="center">
                                                            <Chip 
                                                                label={d.differenza > 0 ? `+${d.differenza}` : d.differenza}
                                                                color={d.differenza > 0 ? 'info' : 'warning'}
                                                                size="small"
                                                            />
                                                        </MuiTableCell>
                                                    </MuiTableRow>
                                                ))
                                            )}
                                        </MuiTableBody>
                                    </MuiTable>
                                </MuiTableContainer>
                            )}
                            
                            {/* Tab Barcode Non Trovati */}
                            {reportTab === 1 && (
                                <MuiTableContainer sx={{ maxHeight: 300 }}>
                                    <MuiTable stickyHeader>
                                        <MuiTableHead>
                                            <MuiTableRow>
                                                <MuiTableCell>Barcode</MuiTableCell>
                                                <MuiTableCell align="center">Quantità Contata</MuiTableCell>
                                            </MuiTableRow>
                                        </MuiTableHead>
                                        <MuiTableBody>
                                            {(reportData?.barcode_non_trovati?.length || 0) === 0 ? (
                                                <MuiTableRow>
                                                    <MuiTableCell colSpan={2} align="center">
                                                        <CheckIcon color="success" /> Tutti i barcode trovati nel database
                                                    </MuiTableCell>
                                                </MuiTableRow>
                                            ) : (
                                                (reportData?.barcode_non_trovati || []).map((b, i) => (
                                                    <MuiTableRow key={i}>
                                                        <MuiTableCell>
                                                            <Typography variant="body2" fontFamily="monospace" color="error">
                                                                {b.barcode}
                                                            </Typography>
                                                        </MuiTableCell>
                                                        <MuiTableCell align="center">{b.quantita_contata}</MuiTableCell>
                                                    </MuiTableRow>
                                                ))
                                            )}
                                        </MuiTableBody>
                                    </MuiTable>
                                </MuiTableContainer>
                            )}
                            
                            {/* Tab Prodotti Non Scansionati */}
                            {reportTab === 2 && (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Questi prodotti hanno quantità &gt; 0 a sistema ma non sono stati scansionati.
                                        Potrebbero essere mancanti o in una posizione diversa.
                                    </Alert>
                                    <MuiTableContainer sx={{ maxHeight: 300 }}>
                                        <MuiTable stickyHeader>
                                            <MuiTableHead>
                                                <MuiTableRow>
                                                    <MuiTableCell>Barcode</MuiTableCell>
                                                    <MuiTableCell>Titolo</MuiTableCell>
                                                    <MuiTableCell>Tipo</MuiTableCell>
                                                    <MuiTableCell>Ubicazione</MuiTableCell>
                                                    <MuiTableCell align="center">Quantità Sistema</MuiTableCell>
                                                </MuiTableRow>
                                            </MuiTableHead>
                                            <MuiTableBody>
                                                {(reportData?.prodotti_non_scansionati?.length || 0) === 0 ? (
                                                    <MuiTableRow>
                                                        <MuiTableCell colSpan={5} align="center">
                                                            <CheckIcon color="success" /> Tutti i prodotti sono stati scansionati
                                                        </MuiTableCell>
                                                    </MuiTableRow>
                                                ) : (
                                                    (reportData?.prodotti_non_scansionati || []).map((p, i) => (
                                                        <MuiTableRow key={i}>
                                                            <MuiTableCell>
                                                                <Typography variant="body2" fontFamily="monospace">{p.barcode}</Typography>
                                                            </MuiTableCell>
                                                            <MuiTableCell>{p.titolo}</MuiTableCell>
                                                            <MuiTableCell>
                                                                <Chip label={p.tipo_prodotto} size="small" />
                                                            </MuiTableCell>
                                                            <MuiTableCell>{p.ubicazione || '-'}</MuiTableCell>
                                                            <MuiTableCell align="center">
                                                                <Chip label={p.quantita_sistema} color="warning" size="small" />
                                                            </MuiTableCell>
                                                        </MuiTableRow>
                                                    ))
                                                )}
                                            </MuiTableBody>
                                        </MuiTable>
                                    </MuiTableContainer>
                                </>
                            )}
                            
                            {/* Opzione azzera non scansionati */}
                            {(reportData?.prodotti_non_scansionati?.length || 0) > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch 
                                                checked={azzeraNonScansionati} 
                                                onChange={(e) => setAzzeraNonScansionati(e.target.checked)}
                                                color="warning"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" color="warning.main">
                                                Azzera quantità dei {(reportData?.prodotti_non_scansionati?.length || 0)} prodotti non scansionati
                                            </Typography>
                                        }
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowReportDialog(false)}>
                        Annulla
                    </Button>
                    <Button 
                        variant="contained" 
                        color="success"
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
                        onClick={() => setConfirmCommit(true)}
                        disabled={loading}
                    >
                        Conferma e Aggiorna Database
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Dialog Conferma Finale */}
            <Dialog open={confirmCommit} onClose={() => setConfirmCommit(false)}>
                <DialogTitle color="warning.main">
                    <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Conferma Aggiornamento
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Stai per aggiornare le quantità nel database per {zonaSelezionata ? <strong>la zona "{zonaSelezionata}"</strong> : "tutto il magazzino"}.
                    </Typography>
                    <Typography>Questa operazione:</Typography>
                    <ul>
                        <li>Aggiornerà le quantità di {(reportData?.discrepanze?.length || 0)} prodotti con discrepanze.</li>
                        {azzeraNonScansionati && (reportData?.prodotti_non_scansionati?.length || 0) > 0 && (
                            <li style={{ color: 'orange' }}>
                                Azzererà {(reportData?.prodotti_non_scansionati?.length || 0)} prodotti non scansionati {zonaSelezionata ? <strong>in questa zona</strong> : ""}.
                            </li>
                        )}
                    </ul>
                    <Typography color="text.secondary" variant="body2">
                        L'operazione sarà registrata nel log per future verifiche.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCommit(false)}>
                        Annulla
                    </Button>
                    <Button 
                        variant="contained" 
                        color="warning"
                        onClick={handleCommitInventario}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={18} /> : <CheckIcon />}
                    >
                        Conferma Aggiornamento
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Dialog Storico */}
            <Dialog open={showHistory} onClose={() => setShowHistory(false)} maxWidth="md" fullWidth>
                <DialogTitle>Storico Inventari</DialogTitle>
                <DialogContent>
                    <MuiTableContainer>
                        <MuiTable>
                            <MuiTableHead>
                                <MuiTableRow>
                                    <MuiTableCell>Data</MuiTableCell>
                                    <MuiTableCell>Utente</MuiTableCell>
                                    <MuiTableCell>Categoria</MuiTableCell>
                                    <MuiTableCell align="center">Scansioni</MuiTableCell>
                                    <MuiTableCell align="center">Prodotti</MuiTableCell>
                                    <MuiTableCell align="center">Modifiche</MuiTableCell>
                                </MuiTableRow>
                            </MuiTableHead>
                            <MuiTableBody>
                                {historyData.length === 0 ? (
                                    <MuiTableRow>
                                        <MuiTableCell colSpan={6} align="center">Nessun inventario registrato</MuiTableCell>
                                    </MuiTableRow>
                                ) : (
                                    historyData.map((log) => (
                                        <MuiTableRow key={log.id}>
                                            <MuiTableCell>
                                                {new Date(log.data_fine).toLocaleString()}
                                            </MuiTableCell>
                                            <MuiTableCell>{log.utente}</MuiTableCell>
                                            <MuiTableCell>
                                                <Chip label={log.categoria || 'Tutti'} size="small" />
                                            </MuiTableCell>
                                            <MuiTableCell align="center">{log.totale_scansioni}</MuiTableCell>
                                            <MuiTableCell align="center">{log.totale_prodotti}</MuiTableCell>
                                            <MuiTableCell align="center">
                                                <Chip 
                                                    label={log.totale_modifiche} 
                                                    color={log.totale_modifiche > 0 ? 'warning' : 'default'}
                                                    size="small" 
                                                />
                                            </MuiTableCell>
                                        </MuiTableRow>
                                    ))
                                )}
                            </MuiTableBody>
                        </MuiTable>
                    </MuiTableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowHistory(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            
            {/* Dialog Multi-SKU */}
            <InventarioMultiSKUDialog
                open={showMultiSKUDialog}
                onClose={() => {
                    setShowMultiSKUDialog(false);
                    setCurrentBarcodeData(null);
                    if (inputRef.current && scanning) {
                        setTimeout(() => inputRef.current.focus(), 100);
                    }
                }}
                barcodeData={currentBarcodeData}
                onConfirm={handleMultiSKUConfirm}
            />

            {/* Dialog Report Sincronizzazione Mirakl */}
            <Dialog open={!!miraklReport} onClose={() => setMiraklReport(null)}>
                <DialogTitle>Report Sincronizzazione IBS/Mirakl</DialogTitle>
                <DialogContent>
                    {miraklReport ? (
                        <Box>
                            <Typography gutterBottom>
                                La sincronizzazione delle offerte su IBS/Mirakl è stata eseguita.
                            </Typography>
                            {miraklReport.error ? (
                                <Alert severity="error">
                                    Si è verificato un errore: {miraklReport.error}
                                </Alert>
                            ) : (
                                <List>
                                    <ListItem>
                                        <ListItemIcon><SyncIcon /></ListItemIcon>
                                        <ListItemText primary="Prodotti elaborati" secondary={miraklReport.total_processed} />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="Offerte aggiornate/create" secondary={miraklReport.success_updated + miraklReport.success_created} />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                                        <ListItemText primary="Errori" secondary={miraklReport.errors} />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><BlockIcon /></ListItemIcon>
                                        <ListItemText primary="Saltati (non pubblicabili)" secondary={miraklReport.skipped} />
                                    </ListItem>
                                </List>
                            )}
                        </Box>
                    ) : (
                        <CircularProgress />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMiraklReport(null)}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Crea Nuovo Prodotto */}
            <Dialog open={showCreateProductDialog} onClose={() => setShowCreateProductDialog(false)}>
                <DialogTitle>Prodotto non Trovato</DialogTitle>
                <DialogContent>
                    <Typography>
                        Il barcode <strong>{barcodeToCreate}</strong> non è stato trovato nel database.
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Vuoi creare un nuovo prodotto?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCreateProductDialog(false)}>Annulla</Button>
                    <Button 
                        onClick={() => {
                            // Rimuove il barcode "non trovato" per evitare conteggi doppi
                            setConteggiDettagliati(prev => prev.filter(item => item.barcode !== barcodeToCreate));
                            navigate('/nuovo/film', { state: { barcode: barcodeToCreate, from: 'inventory', wasScanning: scanning } });
                            setShowCreateProductDialog(false);
                        }}
                    >
                        Crea Film
                    </Button>
                    <Button 
                        onClick={() => {
                            // Rimuove il barcode "non trovato" per evitare conteggi doppi
                            setConteggiDettagliati(prev => prev.filter(item => item.barcode !== barcodeToCreate));
                            navigate('/nuovo/musica', { state: { barcode: barcodeToCreate, from: 'inventory', wasScanning: scanning } });
                            setShowCreateProductDialog(false);
                        }}
                        variant="contained"
                    >
                        Crea Musica
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default InventarioPage;
